// Core imports
import React, { Fragment, forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { t } from 'i18next';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';

// Component imports
import { Button, Col, Collapse, Empty, Row, Select, Spin, Tabs, Typography } from 'antd';
import GeneralResultsTable from '../Tables/TestSetResultsTable/TestResultsTable';
import FormSectionHeader from '../UI/FormSectionHeader';
import ClassificationReportTable from '../Tables/TestSetResultsTable/ClassificationReportTable';
import IntentsResultsTable from '../Tables/TestSetResultsTable/IntentsResultsTable';
import ConfusionTable from '../Tables/TestSetResultsTable/ConfusionTable';

// Misc imports
import moment from 'moment-timezone';
import { useLazyExportTestSetResultsQuery, useLazyTestSetResultQuery, useTestSetResultsQuery } from '../../store/testSets/testSetsApi';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useFetchNlpPipelinesQuery } from '../../store/nlpPipelines/nlpPipelinesApi';
import { isEmptyArray } from '../../utils/is/isEmptyArray';

const formatDate = (date, timezone = 'UTC') => {
  return moment(date).tz(timezone).format('YYYY-MM-DD, HH:mm');
};

const TestSetResultsForm = forwardRef(({
  activeTestSetId = null,
  setIsLoading = () => false,
  ...props
}, ref) => {
  const { orgId, tenantId } = useParams();
  const [fetchedTestResults, setFetchedTestResults] = useState(new Map());
  const [selectedTestResultId, setSelectedTestResultId] = useState(null);
  const [testResultInfo, setTestResultInfo] = useState(null);
  const [tabsActiveKey, setTabsActiveKey] = useState(null);
  const [isCurrentlyRunningTest, setIsCurrentlyRunningTest] = useState(false);

  const [exportTestSetResults, { isLoading: isExportingTestResult }] = useLazyExportTestSetResultsQuery();
  const { data: nlpPipelines, isFetching: isFetchingNlpPipelines } = useFetchNlpPipelinesQuery(
    {
      orgId,
      tenantId
    },
    { selectFromResult: ({ data }) => {
      const itemsMap = new Map();

      data?.items?.forEach(item => {
        itemsMap.set(item?.id, item);
      });

      return {
        data: {
          ...data,
          itemsMap
        }
      };
    } }
  );

  const { data: testResults, isLoading: isFetchingTestResults } = useTestSetResultsQuery({ orgId, tenantId, testSetId: activeTestSetId, urlParams: {
    page: 0,
    pageSize: 500,
    sortBy: 'createdAt',
    order: 'desc'
  } });
  const { data: bot } = getCurrentQueryState('fetchBot', { orgId, tenantId });
  const [fetchTestSetResult, { isFetching: isFetchingTestResult }] = useLazyTestSetResultQuery();

  const getSelectedTestResultInfo = async (testResultId, forceFetch = false) => {
    if(!forceFetch && fetchedTestResults.has(testResultId)) {
      return fetchedTestResults.get(testResultId);
    }

    const fetchedTestResult = await fetchTestSetResult({ orgId: orgId, tenantId: tenantId, nlpTestId: activeTestSetId, runId: testResultId }).unwrap();

    if(!fetchedTestResult) {
      return null;
    }

    setFetchedTestResults((prev) => new Map(prev).set(fetchedTestResult.id, fetchedTestResult));
    return fetchedTestResult;
  };

  useEffect(() => {
    if (!selectedTestResultId && testResults?.items?.length > 0) {
      setSelectedTestResultId(testResults?.items?.[0]?.id);
    }
  }, [testResults, selectedTestResultId]);

  const fetchTestResult = async (forceFetch = false) => {
    if (selectedTestResultId) {
      const testResult = await getSelectedTestResultInfo(selectedTestResultId, forceFetch);
      setTestResultInfo(testResult);
    }
  };

  useEffect(() => {
    fetchTestResult();
  }, [selectedTestResultId]);

  useEffect(() => {
    if(!testResultInfo) {
      return;
    }

    setTabsActiveKey(testResultInfo?.results?.[0]?.nlpPipelineId);
  }, [testResultInfo]);

  useEffect(() => {
    let runningTestInterval = null;
    if(testResultInfo?.status === 'RUNNING') {
      setIsCurrentlyRunningTest(true);

      runningTestInterval = setInterval(() => {
        if(testResultInfo?.status !== 'RUNNING') {
          setIsCurrentlyRunningTest(false);
          clearInterval(runningTestInterval);
          return;
        }
        fetchTestResult(true);
      }, 3000);
    } else {
      setIsCurrentlyRunningTest(false);
    }

    return () => {
      setIsCurrentlyRunningTest(false);
      clearInterval(runningTestInterval);
    };
  }, [testResultInfo?.status, selectedTestResultId]);

  const onTabsChange = (activeKey) => {
    setTabsActiveKey(activeKey);
  };

  const onTestSetExport = async () => {
    await exportTestSetResults({
      orgId: orgId,
      tenantId: tenantId,
      testSetId: activeTestSetId,
      id: testResultInfo?.id
    });
  };

  useImperativeHandle(ref, () => ({
    renderFooter: ({ disabled }) => renderEditFooter(disabled)
  }));

  const renderEditFooter = (disabled) => {
    return (
      <Row justify="end" gutter={8}>
        <Col>
          <Button
            type="primary"
            onClick={onTestSetExport}
            size="large"
            loading={isExportingTestResult}
            disabled={isEmptyArray(testResults?.items) || isEmptyArray(testResultInfo?.results) || isExportingTestResult || isCurrentlyRunningTest || isFetchingTestResult || isFetchingTestResults || isFetchingNlpPipelines || disabled}
          >
            {t('Download')}
          </Button>
        </Col>
      </Row>
    );
  };

  const renderSelectOptionLabel = (date, timezone = 'UTC') => (
    <Typography.Text className="fs-base" strong>
      {`Test run: ${formatDate(date, timezone)}`}
    </Typography.Text>
  );

  const selectOptions = useMemo(() => {
    if(!testResults?.items?.length) {
      return;
    }

    return testResults?.items?.map((result) => (
      <Select.Option
        key={`${result.id}`}
        value={result.id}
        label={
          <Fragment>
            {renderSelectOptionLabel(result?.createdAt, bot?.timezone)}
          </Fragment>
        }
      >
        {renderSelectOptionLabel(result?.createdAt, bot?.timezone)}
      </Select.Option>
    ));
  }, [testResults?.items, activeTestSetId]);

  const renderResultsTables = (results) => (
    <Fragment>
      <Collapse defaultActiveKey={['2']} ghost>
        <Collapse.Panel
          showArrow={false}
          collapsible="icon"
          header={<FormSectionHeader title={t('Model Performance Analysis (Issues)')} uppercase collapsible={false} />}
          key="2"
        >
          <IntentsResultsTable runTestSetResults={results}/>
        </Collapse.Panel>
      </Collapse>
      <Collapse defaultActiveKey={['2']} ghost>
        <Collapse.Panel
          showArrow={false}
          collapsible="icon"
          header={<FormSectionHeader title={t('Classification Report')} uppercase collapsible={false} />}
          key="2"
        >
          <ClassificationReportTable runTestSetResults={results}/>
        </Collapse.Panel>
      </Collapse>
      <Collapse defaultActiveKey={['2']} ghost>
        <Collapse.Panel
          showArrow={false}
          collapsible="icon"
          header={<FormSectionHeader title={t('Confusion Matrix (full screen mode view recommended)')} uppercase collapsible={false} />}
          key="2"
        >
          <ConfusionTable runTestSetResults={results}/>
        </Collapse.Panel>
      </Collapse>
    </Fragment>
  );

  const renderTestResultContent = () => {
    if(!isFetchingTestResults && isEmptyArray(testResults?.items)) {
      return (
        <Col xs={24}>
          <Empty
            description={t('There are no available Test Results')}
          />
        </Col>
      );
    }

    if(isCurrentlyRunningTest) {
      return (
        <Col xs={24} className="justify-content-center d-flex">
          <Spin spinning tip={t('Test is still running')}/>
        </Col>
      );
    }

    if(isFetchingTestResults || isFetchingTestResult || isFetchingNlpPipelines) {
      return (
        <Col xs={24} className="justify-content-center d-flex">
          <Spin spinning tip={t('Loading Test Run Results')}/>
        </Col>
      );
    }

    if(isEmptyArray(testResultInfo?.results)) {
      return (
        <Col xs={24}>
          <Empty
            description={(
              <Typography.Paragraph>
                {t('No data available for this test run.')}
              </Typography.Paragraph>
            )}
          />
        </Col>
      );
    }

    return (
      <Fragment>
        <Col xs={24}>
          <GeneralResultsTable runTestSetResults={testResultInfo?.results}/>
        </Col>

        <Col xs={24}>
          <Tabs
            defaultActiveKey={testResultInfo?.results?.[0]?.nlpPipelineId}
            type="card"
            activeKey={tabsActiveKey}
            onChange={onTabsChange}
            items={testResultInfo?.results?.map(resultItem => {
              const pipelineName = nlpPipelines?.itemsMap?.get(resultItem?.nlpPipelineId).name;
              return {
                key: resultItem?.nlpPipelineId,
                label: pipelineName,
                children: renderResultsTables(resultItem)
              };
            })}
          />
        </Col>
      </Fragment>
    );
  };

  return (
    <Fragment>
      <Collapse defaultActiveKey={['2']} ghost>
        <Collapse.Panel
          showArrow={false}
          collapsible="icon"
          header={
            <Row className="d-flex align-items-center" gutter={16}>
              <Col xs={18}>
                <FormSectionHeader title={t('Test Results')} uppercase collapsible={false} />
              </Col>
              <Col xs={6} className="d-flex justify-content-end">
                <Select
                  className="w-100"
                  style={{ minWidth: '230px' }}
                  placeholder={t('Please select a Test Run')}
                  getPopupContainer={triggerNode => triggerNode.parentElement}
                  size="middle"
                  popupClassName="scrollbar-custom overflow-y-auto missed-question-details-dropdown"
                  optionLabelProp="label"
                  value={selectedTestResultId}
                  onSelect={(value) => {
                    setSelectedTestResultId(value);
                  }}
                >
                  {selectOptions}
                </Select>
              </Col>
            </Row>
          }
          key="2"
        >
          <Row>
            {renderTestResultContent()}
          </Row>
        </Collapse.Panel>
      </Collapse>
    </Fragment>
  );
});

TestSetResultsForm.propTypes = {
  activeTestSetId: PropTypes.string,
  setIsLoading: PropTypes.func
};

export default TestSetResultsForm;
